import { useCallback, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { moveArrayItem } from '../Forms/lib/listCatalogColumns';

/** Insertion rule shown between rows while dragging. */
export function CatalogPickerDropLine() {
  return (
    <Box
      role="presentation"
      aria-hidden
      sx={{
        position: 'relative',
        height: 0,
        mx: 0.5,
        my: 0.5,
        pointerEvents: 'none',
        '&::before': {
          content: '""',
          display: 'block',
          height: 2,
          borderRadius: 1,
          bgcolor: 'primary.main',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: -3,
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: 'primary.main',
        },
      }}
    />
  );
}

export function resolveCatalogDropIndex(fromIndex, insertAt) {
  if (fromIndex === null || insertAt === null) {
    return fromIndex;
  }
  if (fromIndex < insertAt) {
    return insertAt - 1;
  }
  return insertAt;
}

/** Shared drag-reorder state for catalog picker dialogs. */
export function useCatalogDragReorder({
  orderedIds,
  setOrderedIds,
  setOrderTouched,
  saving,
  minDropIndex = 0,
  canDragFromIndex = () => true,
  canDropAtIndex = index => index >= minDropIndex,
}) {
  const [dragIndex, setDragIndex] = useState(null);
  const [dropAt, setDropAt] = useState(null);
  const dragIndexRef = useRef(null);

  const clearDragState = useCallback(() => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setDropAt(null);
  }, []);

  const resetDragState = useCallback(() => {
    dragIndexRef.current = null;
    setDragIndex(null);
    setDropAt(null);
  }, []);

  const handleMove = useCallback(
    (fromIndex, toIndex) => {
      if (!canDragFromIndex(fromIndex) || !canDropAtIndex(toIndex)) {
        return;
      }
      setOrderTouched(true);
      setOrderedIds(prev => moveArrayItem(prev, fromIndex, toIndex));
    },
    [canDragFromIndex, canDropAtIndex, setOrderTouched, setOrderedIds],
  );

  const handleDragStart = useCallback(
    (event, index) => {
      if (saving || !canDragFromIndex(index)) {
        return;
      }
      dragIndexRef.current = index;
      setDragIndex(index);
      setDropAt(null);
      const { dataTransfer } = event;
      if (dataTransfer) {
        // eslint-disable-next-line no-param-reassign
        dataTransfer.effectAllowed = 'move';
        dataTransfer.setData('text/plain', String(index));
      }
    },
    [saving, canDragFromIndex],
  );

  const handleRowDragOver = useCallback(
    (event, index) => {
      event.preventDefault();
      if (event.dataTransfer) {
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.dropEffect = 'move';
      }
      const fromIndex = dragIndexRef.current;
      if (fromIndex === null || !canDragFromIndex(fromIndex)) {
        setDropAt(null);
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const insertBefore = event.clientY < rect.top + rect.height / 2;
      const nextDropAt = insertBefore ? index : index + 1;
      if (nextDropAt === fromIndex || nextDropAt === fromIndex + 1) {
        setDropAt(null);
        return;
      }
      if (!canDropAtIndex(nextDropAt)) {
        setDropAt(null);
        return;
      }
      setDropAt(nextDropAt);
    },
    [canDragFromIndex, canDropAtIndex],
  );

  const handleListDragOver = useCallback(
    event => {
      event.preventDefault();
      if (event.dataTransfer) {
        // eslint-disable-next-line no-param-reassign
        event.dataTransfer.dropEffect = 'move';
      }
      if (dragIndexRef.current === null) {
        return;
      }
      const nextDropAt = orderedIds.length;
      if (canDropAtIndex(nextDropAt)) {
        setDropAt(nextDropAt);
      }
    },
    [orderedIds.length, canDropAtIndex],
  );

  const handleRowDrop = useCallback(
    event => {
      event.preventDefault();
      const fromIndex = dragIndexRef.current;
      if (fromIndex === null || dropAt === null) {
        clearDragState();
        return;
      }
      const toIndex = resolveCatalogDropIndex(fromIndex, dropAt);
      if (toIndex !== fromIndex && canDropAtIndex(toIndex)) {
        handleMove(fromIndex, toIndex);
      }
      clearDragState();
    },
    [dropAt, clearDragState, canDropAtIndex, handleMove],
  );

  return {
    dragIndex,
    dropAt,
    clearDragState,
    resetDragState,
    handleDragStart,
    handleRowDragOver,
    handleListDragOver,
    handleRowDrop,
    handleMove,
  };
}

const guideTitleId = 'catalog-picker-guide-title';

/** Always-visible 3-step guide for catalog picker dialogs. */
export function CatalogPickerGuide({ title, steps, note }) {
  return (
    <Box
      sx={{
        bgcolor: 'action.hover',
        borderRadius: 1,
        p: 1.5,
        mb: 2,
      }}>
      <Typography
        id={guideTitleId}
        variant="subtitle2"
        fontWeight={600}
        sx={{ mb: 1.5 }}>
        {title}
      </Typography>
      <Stack
        component="ol"
        aria-labelledby={guideTitleId}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {steps.map((step, index) => {
          const StepIcon = step.icon;
          return (
            <Box
              component="li"
              key={step.title}
              sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 1 }}>
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                }}>
                <Box
                  sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: 1,
                  }}>
                  {index + 1}
                </Box>
                {StepIcon && (
                  <StepIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ lineHeight: 1.3 }}>
                  {step.title}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}>
                  {step.description}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
      {note && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1.5 }}>
          {note}
        </Typography>
      )}
    </Box>
  );
}

export function CatalogPickerRow({
  id,
  index,
  label,
  checked,
  checkboxDisabled,
  onToggle,
  isDragging,
  showLineBefore,
  canReorder,
  canMoveUp,
  canMoveDown,
  showMoveButtons = true,
  dragHandleHidden = false,
  onDragStart,
  onDragEnd,
  onRowDragOver,
  onRowDrop,
  onMoveUp,
  onMoveDown,
  moveUpLabel,
  moveDownLabel,
  tourAnchors,
}) {
  const rowTourAttr = tourAnchors?.row ? { 'data-tour': tourAnchors.row } : {};
  const dragTourAttr = tourAnchors?.drag
    ? { 'data-tour': tourAnchors.drag }
    : {};
  const arrowsTourAttr = tourAnchors?.arrows
    ? { 'data-tour': tourAnchors.arrows }
    : {};

  return (
    <Box key={id} {...rowTourAttr}>
      {showLineBefore && <CatalogPickerDropLine />}
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        onDragOver={event => onRowDragOver(event, index)}
        onDrop={onRowDrop}
        sx={{
          opacity: isDragging ? 0.4 : 1,
          borderRadius: 1,
          px: 0.5,
          outline: isDragging ? '1px dashed' : 'none',
          outlineColor: 'primary.light',
          bgcolor: isDragging ? 'action.selected' : 'transparent',
          transition: theme =>
            theme.transitions.create(['opacity', 'background-color'], {
              duration: theme.transitions.duration.shortest,
            }),
          '@media (prefers-reduced-motion: reduce)': {
            transition: 'none',
          },
          '&:hover': {
            bgcolor: isDragging ? 'action.selected' : 'action.hover',
          },
        }}>
        <Box
          component="span"
          draggable={canReorder}
          onDragStart={event => onDragStart(event, index)}
          onDragEnd={onDragEnd}
          {...dragTourAttr}
          sx={{
            display: 'inline-flex',
            color: dragHandleHidden ? 'action.disabled' : 'text.secondary',
            cursor: canReorder ? 'grab' : 'default',
            touchAction: 'none',
            visibility: dragHandleHidden ? 'hidden' : 'visible',
            '&:active': {
              cursor: canReorder ? 'grabbing' : 'default',
            },
          }}
          aria-hidden>
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <FormControlLabel
          sx={{ flex: 1, mr: 0, my: 0 }}
          control={
            <Checkbox
              checked={checked}
              disabled={checkboxDisabled}
              onChange={onToggle}
            />
          }
          label={label}
        />
        {showMoveButtons && (
          <Box
            component="span"
            {...arrowsTourAttr}
            sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Tooltip title={moveUpLabel} arrow placement="top">
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={!canMoveUp}
                  aria-label={moveUpLabel}
                  onClick={onMoveUp}>
                  <KeyboardArrowUpIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title={moveDownLabel} arrow placement="top">
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  disabled={!canMoveDown}
                  aria-label={moveDownLabel}
                  onClick={onMoveDown}>
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
